import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Outlet, useMatch } from "react-router-dom";
import { useDashboards } from "../api/hooks";
import { humanizeDashboardId, useDocumentTitle } from "../hooks/useDocumentTitle";
import { fullscreenElement, requestPresentFullscreen, usePresentMode, usePresentScale } from "../hooks/usePresentMode";
import { Sidebar } from "./Sidebar";

export function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const { present, setPresent } = usePresentMode();
  const presentScale = usePresentScale();
  const dashboardMatch = useMatch("/d/:id");
  const dashboardId = dashboardMatch?.params.id;
  const { data: dashboards } = useDashboards();
  const dashboardName = dashboardId
    ? dashboards?.find((dashboard) => dashboard.id === dashboardId)?.name ?? humanizeDashboardId(dashboardId)
    : "Dashboards";
  useDocumentTitle(dashboardName);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const hadFullscreen = useRef(false);

  useEffect(() => {
    const syncFullscreen = () => {
      const active = !!fullscreenElement();
      setIsFullscreen(active);
      // Esc (or the browser control) leaves fullscreen first — drop present with it.
      // Bookmarked `?present=1` never entered fullscreen, so do not treat that as an exit.
      if (!active && present && hadFullscreen.current) setPresent(false);
      hadFullscreen.current = active;
    };
    syncFullscreen();
    document.addEventListener("fullscreenchange", syncFullscreen);
    document.addEventListener("webkitfullscreenchange", syncFullscreen);
    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreen);
      document.removeEventListener("webkitfullscreenchange", syncFullscreen);
    };
  }, [present, setPresent]);

  useEffect(() => {
    if (!present) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPresent(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [present, setPresent]);

  return (
    <div
      className="shell"
      data-sidebar-collapsed={collapsed}
      data-present={present}
      style={present ? ({ "--present-scale": String(presentScale) } as CSSProperties) : undefined}
    >
      {!present && <Sidebar onToggleCollapsed={() => setCollapsed((c) => !c)} />}
      <main className="main">
        <Outlet />
      </main>
      {present && (
        <div className="present-controls">
          {!isFullscreen && (
            <button
              className="present-exit present-exit-emphasis"
              type="button"
              title="Enter fullscreen"
              onClick={() => void requestPresentFullscreen()}
            >
              <i className="ri-fullscreen-line" />
              Fullscreen
            </button>
          )}
          <button className="present-exit" type="button" title="Exit present (Esc)" onClick={() => setPresent(false)}>
            <i className="ri-fullscreen-exit-line" />
            Exit
          </button>
        </div>
      )}
    </div>
  );
}
